import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import PropertyComposition from "../PropertyComposition";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomAutocomplete,
  CustomThreeColInputGroup,
  PostalCodeAutoAddressInput,
  FixedActionBar,
  CustomButton,
  PropertyRegistrationTabs,
  DocumentUploadAccordion,
  CustomFileUploader,
  CustomFormNavigationGuard,
  CustomFileModal,
  CustomCategorySelectSingle,
  CustomSingleSelect,
  useScrollToFirstError,
  type CategoryOption,
} from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchDocumentManagement,
  upsertDocumentManagement,
} from "../../../store/documentManagementSlice";
import {
  fetchGroupOptionManagement,
  upsertGroupOptionManagement,
} from "../../../store/groupOptionManagementSlice";
import { PropertyFormValues } from "../../../types";
import {
  extractS3KeyFromUrl,
  getApiBaseUrl,
  getContentTypeFromFileName,
  processJsonResponse,
  createBlobAndDownload,
} from "@ippon/shared-utils";
import { getAuthToken } from "@/utils/authUtils";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { LabelHelpTooltip } from "@/common/LabelHelpTooltip";
import { propertyDocumentGroups } from "../../../constants/documentGroups";
import LotBoundaryManager from "../LotBoundaryManager";
import { reverseLookupPostalCode } from "@/utils/postalCodeLookup";
import {
  DEFAULT_PROPERTY_OVERVIEW_SUBTAB_ID,
  PROPERTY_OVERVIEW_SUBTABS,
  parseTabId,
} from "../tabConfig";

// オプション定義をcreateページから移植

const prefectureOptions = [
  { label: "北海道", value: "北海道" },
  { label: "青森県", value: "青森県" },
  { label: "岩手県", value: "岩手県" },
  { label: "宮城県", value: "宮城県" },
  { label: "秋田県", value: "秋田県" },
  { label: "山形県", value: "山形県" },
  { label: "福島県", value: "福島県" },
  { label: "茨城県", value: "茨城県" },
  { label: "栃木県", value: "栃木県" },
  { label: "群馬県", value: "群馬県" },
  { label: "埼玉県", value: "埼玉県" },
  { label: "千葉県", value: "千葉県" },
  { label: "東京都", value: "東京都" },
  { label: "神奈川県", value: "神奈川県" },
  { label: "新潟県", value: "新潟県" },
  { label: "富山県", value: "富山県" },
  { label: "石川県", value: "石川県" },
  { label: "福井県", value: "福井県" },
  { label: "山梨県", value: "山梨県" },
  { label: "長野県", value: "長野県" },
  { label: "岐阜県", value: "岐阜県" },
  { label: "静岡県", value: "静岡県" },
  { label: "愛知県", value: "愛知県" },
  { label: "三重県", value: "三重県" },
  { label: "滋賀県", value: "滋賀県" },
  { label: "京都府", value: "京都府" },
  { label: "大阪府", value: "大阪府" },
  { label: "兵庫県", value: "兵庫県" },
  { label: "奈良県", value: "奈良県" },
  { label: "和歌山県", value: "和歌山県" },
  { label: "鳥取県", value: "鳥取県" },
  { label: "島根県", value: "島根県" },
  { label: "岡山県", value: "岡山県" },
  { label: "広島県", value: "広島県" },
  { label: "山口県", value: "山口県" },
  { label: "徳島県", value: "徳島県" },
  { label: "香川県", value: "香川県" },
  { label: "愛媛県", value: "愛媛県" },
  { label: "高知県", value: "高知県" },
  { label: "福岡県", value: "福岡県" },
  { label: "佐賀県", value: "佐賀県" },
  { label: "長崎県", value: "長崎県" },
  { label: "熊本県", value: "熊本県" },
  { label: "大分県", value: "大分県" },
  { label: "宮崎県", value: "宮崎県" },
  { label: "鹿児島県", value: "鹿児島県" },
  { label: "沖縄県", value: "沖縄県" },
];
const prefectureCodeMap: Record<string, string> = {
  "1": "北海道",
  "2": "青森県",
  "3": "岩手県",
  "4": "宮城県",
  "5": "秋田県",
  "6": "山形県",
  "7": "福島県",
  "8": "茨城県",
  "9": "栃木県",
  "10": "群馬県",
  "11": "埼玉県",
  "12": "千葉県",
  "13": "東京都",
  "14": "神奈川県",
  "15": "新潟県",
  "16": "富山県",
  "17": "石川県",
  "18": "福井県",
  "19": "山梨県",
  "20": "長野県",
  "21": "岐阜県",
  "22": "静岡県",
  "23": "愛知県",
  "24": "三重県",
  "25": "滋賀県",
  "26": "京都府",
  "27": "大阪府",
  "28": "兵庫県",
  "29": "奈良県",
  "30": "和歌山県",
  "31": "鳥取県",
  "32": "島根県",
  "33": "岡山県",
  "34": "広島県",
  "35": "山口県",
  "36": "徳島県",
  "37": "香川県",
  "38": "愛媛県",
  "39": "高知県",
  "40": "福岡県",
  "41": "佐賀県",
  "42": "長崎県",
  "43": "熊本県",
  "44": "大分県",
  "45": "宮崎県",
  "46": "鹿児島県",
  "47": "沖縄県",
};

// 都市計画系のオプション定義
const usageZoneOptions = [
  { label: "無指定", value: "無指定" },
  { label: "第一種低層住居専用地域", value: "第一種低層住居専用地域" },
  { label: "第二種低層住居専用地域", value: "第二種低層住居専用地域" },
  { label: "第一種中高層住居専用地域", value: "第一種中高層住居専用地域" },
  { label: "第二種中高層住居専用地域", value: "第二種中高層住居専用地域" },
  { label: "第一種住居地域", value: "第一種住居地域" },
  { label: "第二種住居地域", value: "第二種住居地域" },
  { label: "準住居地域", value: "準住居地域" },
  { label: "近隣商業地域", value: "近隣商業地域" },
  { label: "商業地域", value: "商業地域" },
  { label: "準工業地域", value: "準工業地域" },
  { label: "工業地域", value: "工業地域" },
  { label: "工業専用地域", value: "工業専用地域" },
];

const firePreventionOptions = [
  { label: "無指定", value: "無指定" },
  { label: "防火地域", value: "防火地域" },
  { label: "準防火地域", value: "準防火地域" },
];

const heightDistrictOptions = [
  { label: "無指定", value: "無指定" },
  { label: "高度地区", value: "高度地区" },
];

const buildingCoverageRatioOptions = [
  { label: "30%", value: "30" },
  { label: "40%", value: "40" },
  { label: "50%", value: "50" },
  { label: "60%", value: "60" },
  { label: "70%", value: "70" },
  { label: "80%", value: "80" },
];

const floorAreaRatioOptions = [
  { label: "60%", value: "60" },
  { label: "80%", value: "80" },
  { label: "100%", value: "100" },
  { label: "150%", value: "150" },
  { label: "200%", value: "200" },
  { label: "300%", value: "300" },
  { label: "400%", value: "400" },
];

// 建築・土地関連のオプション定義
const houseRegistrationNumberOptions = [
  { label: "あり", value: "あり" },
  { label: "なし", value: "なし" },
];

const buildingTypeOptions = [
  { label: "戸建住宅", value: "戸建住宅" },
  { label: "共同住宅", value: "共同住宅" },
  { label: "店舗・事務所", value: "店舗・事務所" },
  { label: "工場・倉庫", value: "工場・倉庫" },
  { label: "その他", value: "その他" },
];

const structureOptions = [
  { label: "木造", value: "木造" },
  { label: "鉄骨造", value: "鉄骨造" },
  { label: "RC造", value: "RC造" },
  { label: "SRC造", value: "SRC造" },
  { label: "鉄筋コンクリート造", value: "鉄筋コンクリート造" },
];

const propertyCategoryOptions = [
  { label: "レジデンス", value: "residence" },
  { label: "ビルディング", value: "building" },
];

const landCategoryOptions = [
  { label: "宅地", value: "宅地" },
  { label: "田", value: "田" },
  { label: "畑", value: "畑" },
  { label: "山林", value: "山林" },
  { label: "雑種地", value: "雑種地" },
];

const currentConditionOptions = [
  { label: "更地", value: "更地" },
  { label: "建物あり", value: "建物あり" },
  { label: "耕作中", value: "耕作中" },
  { label: "山林", value: "山林" },
];

const landRightsOptions = [
  { label: "所有権", value: "所有権" },
  { label: "信託受益権", value: "信託受益権" },
  { label: "借地権", value: "借地権" },
  { label: "定期借地権", value: "定期借地権" },
  { label: "所有権＋一部借地", value: "所有権＋一部借地" },
  { label: "信託受益権＋一部借地", value: "信託受益権＋一部借地" },
];

const boundaryConfirmedOptions = [
  { label: "確定済み", value: "確定済み" },
  { label: "未確定", value: "未確定" },
];

interface PropertyOverviewProps {
  data?: any;
  onSubmit: (data: PropertyFormValues) => void | Promise<void>;
}

const PropertyOverview: React.FC<PropertyOverviewProps> = ({
  data,
  onSubmit,
}) => {
  const leftInputContainerWidth = "600px";
  const labelWidth = "120px";
  const { showToast } = useGlobalToast();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  interface LocationState {
    chatDoc?: { fileUrl: string; fileName?: string; label?: string };
    [key: string]: unknown;
  }

  function hasChatDocState(state: unknown): state is LocationState {
    return typeof state === "object" && state !== null && "chatDoc" in state;
  }

  const getCleanedLocationState = React.useCallback((): LocationState => {
    const { chatDoc: _chatDoc, ...restState } =
      (location.state as LocationState) || {};
    return restState;
  }, [location.state]);

  const chatDocFromState = hasChatDocState(location.state)
    ? location.state.chatDoc
    : undefined;
  const docLabel = (chatDocFromState?.label || "").trim();
  const [chatPreviewOpen, setChatPreviewOpen] = React.useState(false);
  const [chatPreviewFile, setChatPreviewFile] = React.useState<{
    url: string;
    name: string;
  } | null>(null);
  const [chatFilePickerOpen, setChatFilePickerOpen] = React.useState(false);
  const [chatFilePickerOptions, setChatFilePickerOptions] = React.useState<
    Array<{ url: string; name: string }>
  >([]);
  const lastOpenedChatPreviewUrlRef = React.useRef<string>("");

  // タブの状態管理 (URL: ?subtab=N)
  const initialSubTab = parseTabId(
    searchParams.get("subtab"),
    PROPERTY_OVERVIEW_SUBTABS,
    DEFAULT_PROPERTY_OVERVIEW_SUBTAB_ID,
  );
  const [currentTab, setCurrentTab] = React.useState(initialSubTab);

  React.useEffect(() => {
    const subTab = parseTabId(
      searchParams.get("subtab"),
      PROPERTY_OVERVIEW_SUBTABS,
      DEFAULT_PROPERTY_OVERVIEW_SUBTAB_ID,
    );
    if (subTab !== currentTab) {
      setCurrentTab(subTab);
    }
  }, [searchParams, currentTab]);

  const handleTabChange = (tabId: number) => {
    setCurrentTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set("subtab", String(tabId));
    // 旧キー (camelCase) が残っていたら掃除
    next.delete("subTab");
    const { showToast, ...cleanState } = (location.state as any) || {};
    navigate(
      { search: `?${next.toString()}` },
      { replace: true, state: cleanState },
    );
  };

  const getCategoryKeyByLabel = React.useCallback(
    (label: string): string | null => {
      const trimmed = label.trim();
      if (!trimmed) return null;
      for (const group of propertyDocumentGroups) {
        for (const category of group.categories) {
          if (category.label === trimmed) {
            return category.key;
          }
        }
      }
      return null;
    },
    [],
  );

  React.useEffect(() => {
    if (!docLabel) return;
    // 書類タブ: 物件構成(0), 詳細情報(1), 書類(2)
    if (currentTab !== 2) return;

    const categoryKey = getCategoryKeyByLabel(docLabel);
    const targetId = categoryKey
      ? `doc-category-${categoryKey}`
      : "doc-category-others";

    requestAnimationFrame(() => {
      const el = document.getElementById(targetId);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [docLabel, currentTab, getCategoryKeyByLabel]);

  // サブタブ構成 (タスク②③ 反映後)
  //   物件構成: 新規 (内訳は ④以降で実装)
  //   詳細情報: 旧「基本情報」+「物件詳細」を統合
  //   書類: 旧「書類」を維持
  const tabs = PROPERTY_OVERVIEW_SUBTABS.slice();

  const decodeDocumentNameFromUrl = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split("/");
      const lastPart = parts[parts.length - 1];
      return decodeURIComponent(lastPart);
    } catch {
      return null;
    }
  };

  // 地番と境界の行管理
  const [lots, setLots] = React.useState<
    Array<{
      lotNumber: string;
      boundaries: Array<{
        adjacentLand: string;
        confirmation: any[];
        encroachment: string;
        encroachmentMemo: any[];
      }>;
    }>
  >([]);

  // 境界書類のダウンロードハンドラ
  const handleDownloadDocument = async (doc: {
    url: string;
    name?: string;
  }) => {
    if (!doc?.url) return;

    const resolvedName =
      doc.name || decodeDocumentNameFromUrl(doc.url) || "document";

    if (doc.url.startsWith("data:")) {
      const directLink = document.createElement("a");
      directLink.href = doc.url;
      directLink.download = resolvedName;
      document.body.appendChild(directLink);
      directLink.click();
      document.body.removeChild(directLink);
      return;
    }

    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = getAuthToken();
      const s3Key = extractS3KeyFromUrl(doc.url);

      if (!s3Key) {
        const fallbackLink = document.createElement("a");
        fallbackLink.href = doc.url;
        fallbackLink.download = resolvedName;
        fallbackLink.target = "_blank";
        fallbackLink.rel = "noopener noreferrer";
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ key: s3Key }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch file for download");
      }

      const contentType =
        response.headers.get("content-type") ||
        getContentTypeFromFileName(resolvedName);
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
      createBlobAndDownload(dataToDownload, contentType, resolvedName);
    } catch (error) {
      console.error("Error downloading document:", error);
      showToast({
        type: "error",
        message: "書類のダウンロードに失敗しました",
      });
    }
  };

  // 接道の行管理
  const [roads, setRoads] = React.useState<
    Array<{ direction: string; boundary_confirmed?: string; width: string }>
  >([{ direction: "", boundary_confirmed: "", width: "" }]);

  const addRoad = () =>
    setRoads((prev) => [
      ...prev,
      { direction: "", boundary_confirmed: "", width: "" },
    ]);
  const removeRoad = (index: number) =>
    setRoads((prev) => prev.filter((_, i) => i !== index));

  // その他書類の管理
  const [otherDocuments, setOtherDocuments] = React.useState<
    Array<{ key: string; label: string }>
  >([]);

  // 削除されたドキュメントを追跡（keyごとの削除されたURL+インデックスの配列）
  const [deletedDocuments, setDeletedDocuments] = React.useState<
    Record<string, Array<{ url: string; index?: number }>>
  >({});

  // 初期の「その他」書類データを保存（削除検出用）
  const initialOtherDocumentsRef = React.useRef<
    Array<{ key: string; label: string }>
  >([]);

  // 「その他」書類の枠定義はクライアント単位 (書類設定ページ > 物件書類タブ) で管理する。
  // 物件ごとの追加・削除はせず、ここでは有効な定義を枠として読むだけ。ファイルは物件ごと。
  const propertyOtherDocDefs = useSelector(
    (s: RootState) =>
      (s as any).documentManagement?.record?.property_other_documents as
        | Array<{ document_type: string; display_name: string; order: number }>
        | undefined,
  );

  React.useEffect(() => {
    if (currentTab !== 2) return;
    dispatch(fetchDocumentManagement() as any);
  }, [dispatch, currentTab]);

  React.useEffect(() => {
    const defs = [...(propertyOtherDocDefs ?? [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((d) => ({ key: d.document_type, label: d.display_name }));
    setOtherDocuments(defs);
    initialOtherDocumentsRef.current = defs;
  }, [propertyOtherDocDefs]);

  // documentManagement レコード全体 (枠 upsert 時の existingId / documents 保持用)。
  const documentManagementRecord = useSelector(
    (s: RootState) => (s as any).documentManagement?.record,
  );

  // その他書類の枠追加 (handleAddOtherDocument) は setValue を使うため、
  // useForm 定義後 (handleFormSubmit 付近) に置く。永続化は保存(更新)時にまとめて upsert。

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
  } = useForm<PropertyFormValues>({
    defaultValues: {
      type: "土地",
      name: "",
      property_category: "",
      group_name: "",
      information_source: "",
      postal_code: "",
      prefecture: "",
      city: "",
      block_number: "",
      price: "",
      remarks: "",
      image_urls: [],
    },
    mode: "onChange",
  });
  const scrollToFirstError = useScrollToFirstError<PropertyFormValues>();

  // ── グループ (予実分析用) ──
  const groupOptionRecord = useSelector(
    (s: RootState) => (s as any).groupOptionManagement.record,
  );
  const [groupOptions, setGroupOptions] = React.useState<CategoryOption[]>([]);
  const groupValue = watch("group_name") || "";

  React.useEffect(() => {
    if (currentTab !== 1) return;
    dispatch(fetchGroupOptionManagement()).catch(() => {});
  }, [dispatch, currentTab]);

  React.useEffect(() => {
    if (!groupOptionRecord) return;
    const groups: string[] = Array.isArray(groupOptionRecord.groups)
      ? groupOptionRecord.groups
      : [];
    setGroupOptions(groups.map((g) => ({ label: g, value: g })));
  }, [groupOptionRecord]);

  // 「グループ」プルダウンへのユーザー追加は **ローカルのみ**。
  // 物件本体の保存 (handleFormSubmit) 成功後に、未登録のグループだけまとめて upsert する。
  // 修繕モーダルのカテゴリ追加と同じ方針: 親エンティティの保存に失敗/キャンセルした場合に
  // 「グループだけサーバに残骸が残る」のを防ぐ。
  const handleAddNewGroup = React.useCallback(
    (newGroup: string) => {
      const trimmed = newGroup.trim();
      if (!trimmed) return;
      const exists = groupOptions.some(
        (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) return;
      setGroupOptions((prev) => [...prev, { label: trimmed, value: trimmed }]);
    },
    [groupOptions],
  );

  // 初期データをdefaultValuesとして保持（タブ間で共有）
  const initialDataRef = React.useRef<PropertyFormValues | null>(null);

  React.useEffect(() => {
    if (data) {
      // 書類フィールドのキー一覧（バックエンドAPIの仕様に合わせる）
      const documentKeys = [
        // 登記、測量関連
        "kozu",
        "land_registry",
        "building_registry",
        "survey_map",
        "boundary_confirmation_docs",
        "encroachment_memo_docs",
        // 建築関連
        "construction_notice",
        "inspection_certificate_docs",
        "completion_drawing",
        "structural_calculation",
        // 売買、契約関連
        "sales_contract",
        "important_matters",
        "owner_change_notice",
        "insurance_certificate_docs",
        // デューデリジェンス関連
        "appraisal_report",
        "engineering_report",
        // 管理契約関連
        "property_management_contract",
        "building_management_contract",
        "electricity_contract",
        "industrial_waste_contract",
        "general_waste_contract",
        "electrical_equipment_contract",
        // 消防、防災関連
        "fire_prevention_plan",
        "overall_fire_prevention_plan",
        "chief_fire_manager_appointment",
        "fire_manager_appointment",
        "specific_building_change",
        "fire_manager_delegation",
        // 信託関連
        "trust_contract",
        "trust_certificate",
        "trust_transfer_consent",
      ];

      const initialValues: PropertyFormValues = {
        type: data.type || "土地",
        name: data.name || "",
        property_category: (data as any).property_category || "",
        group_name: (data as any).group_name || "",
        information_source: data.information_source || "",
        postal_code: data.postal_code || "",
        prefecture: (() => {
          const incoming = data.prefecture;
          if (!incoming && incoming !== 0) return "";
          const normalized = String(incoming);
          if (prefectureOptions.some((option) => option.value === normalized)) {
            return normalized;
          }
          return prefectureCodeMap[normalized] || normalized;
        })(),
        city: data.city || "",
        block_number: data.block_number || "",
        price: data.price ? String(data.price) : "",
        remarks: data.remarks || "",
        // その他書類の項目追加・削除を追跡（ダミーフィールド）
        _otherDocumentsChanged: 0,
        // 土地情報
        lot_number: data.lot_number || "",
        land_category: data.land_category || "",
        current_condition: data.current_condition || "",
        land_rights: data.land_rights || "",
        boundary_confirmed: data.boundary_confirmed || "",
        land_area_registered:
          data.land_area_registered != null
            ? String(data.land_area_registered)
            : "",
        land_area_measured:
          data.land_area_measured != null
            ? String(data.land_area_measured)
            : "",
        // 建物情報
        house_registration_number: data.house_registration_number || "",
        building_type: data.building_type || "",
        structure: data.structure || "",
        basement_floors:
          data.basement_floors != null ? String(data.basement_floors) : "",
        ground_floors:
          data.ground_floors != null ? String(data.ground_floors) : "",
        building_area:
          data.building_area != null ? String(data.building_area) : "",
        private_area:
          data.private_area != null ? String(data.private_area) : "",
        total_floor_area:
          data.total_floor_area != null ? String(data.total_floor_area) : "",
        total_floor_area_measured:
          data.total_floor_area_measured != null
            ? String(data.total_floor_area_measured)
            : "",
        built_date: data.built_date || "",
        inspection_certificate: data.inspection_certificate || "",
        environmental_certification: data.environmental_certification || "",
        // 設備情報
        equipment_electricity: data.equipment_electricity || "",
        equipment_water_supply: data.equipment_water_supply || "",
        equipment_sewerage: data.equipment_sewerage || "",
        equipment_city_gas: data.equipment_city_gas || "",
        // 都市計画情報
        usage_zone: data.usage_zone || "",
        fire_prevention: data.fire_prevention || "",
        height_district: data.height_district || "",
        building_coverage_ratio: data.building_coverage_ratio || "",
        floor_area_ratio: data.floor_area_ratio || "",
      };

      // 物件画像を initialValues に追加
      const imageUrls = (data as any)?.image_urls;
      if (imageUrls && Array.isArray(imageUrls)) {
        (initialValues as any).image_urls = imageUrls;
      } else {
        (initialValues as any).image_urls = [];
      }

      // 書類フィールドを initialValues に追加
      documentKeys.forEach((key) => {
        const docs = (data as any)?.[key];
        if (docs && Array.isArray(docs)) {
          (initialValues as any)[key] = docs;
        } else {
          (initialValues as any)[key] = null;
        }
      });

      // その他書類も initialValues に追加
      if (data.other_documents && typeof data.other_documents === "object") {
        Object.entries(data.other_documents).forEach(
          ([key, value]: [string, any]) => {
            if (value.files && Array.isArray(value.files)) {
              (initialValues as any)[key] = value.files;
            } else {
              (initialValues as any)[key] = null;
            }
          },
        );
      }

      // 地番と境界データの設定
      let initialLots: Array<{
        lotNumber: string;
        boundaries: Array<{
          adjacentLand: string;
          confirmation: any[];
          encroachment: string;
          encroachmentMemo: any[];
        }>;
      }> = [];

      if (data.lots && Array.isArray(data.lots) && data.lots.length > 0) {
        initialLots = data.lots.map((lot: any) => ({
          lotNumber: lot.lotNumber || lot.lot_number || "",
          boundaries:
            lot.boundaries && Array.isArray(lot.boundaries)
              ? lot.boundaries.map((boundary: any) => ({
                  adjacentLand:
                    boundary.adjacentLand || boundary.adjacent_land || "",
                  confirmation: boundary.confirmation || [],
                  encroachment: boundary.encroachment || "",
                  encroachmentMemo:
                    boundary.encroachmentMemo ||
                    boundary.encroachment_memo ||
                    [],
                }))
              : [
                  {
                    adjacentLand: "",
                    confirmation: [],
                    encroachment: "",
                    encroachmentMemo: [],
                  },
                ],
        }));
      } else if (
        data.lot_number ||
        (data.boundaries && Array.isArray(data.boundaries))
      ) {
        // 旧形式のデータ（lot_numberとboundariesが別々）
        initialLots = [
          {
            lotNumber: data.lot_number || "",
            boundaries:
              data.boundaries && Array.isArray(data.boundaries)
                ? data.boundaries.map((boundary: any) => ({
                    adjacentLand:
                      boundary.adjacentLand || boundary.adjacent_land || "",
                    confirmation: boundary.confirmation || [],
                    encroachment: boundary.encroachment || "",
                    encroachmentMemo:
                      boundary.encroachmentMemo ||
                      boundary.encroachment_memo ||
                      [],
                  }))
                : [
                    {
                      adjacentLand: "",
                      confirmation: [],
                      encroachment: "",
                      encroachmentMemo: [],
                    },
                  ],
          },
        ];
      } else {
        // データなしの場合は空配列
        initialLots = [];
      }
      setLots(initialLots);

      // 接道データの設定
      let initialRoads: Array<{
        direction: string;
        boundary_confirmed?: string;
        width: string;
      }> = [];

      // roadsが文字列の場合はJSONパースする
      let roadsData = data.roads;
      if (typeof roadsData === "string" && roadsData.trim()) {
        try {
          roadsData = JSON.parse(roadsData);
        } catch (e) {
          console.error("Failed to parse roads data:", e);
          roadsData = null;
        }
      }

      if (roadsData && Array.isArray(roadsData)) {
        initialRoads =
          roadsData.length > 0
            ? roadsData.map((road: any) => ({
                direction: road.direction ?? "",
                boundary_confirmed: road.boundary_confirmed ?? "",
                width: road.width != null ? String(road.width) : "",
              }))
            : [{ direction: "", boundary_confirmed: "", width: "" }];
      } else {
        initialRoads = [{ direction: "", boundary_confirmed: "", width: "" }];
      }
      setRoads(initialRoads);

      // 初期データにlotsとroadsも含める
      const initialDataWithLotsAndRoads = {
        ...initialValues,
        lots: initialLots,
        roads: initialRoads,
      };

      // 初期データを保存
      initialDataRef.current = initialDataWithLotsAndRoads as any;

      // フォームをリセット（初期データをdefaultValuesとして設定）
      reset(initialDataWithLotsAndRoads as any, { keepDirty: false });

      // 「その他」書類の枠はクライアント定義 (property_other_documents) から設定するため、
      // 物件データ側では枠を作らない。ファイル初期値は上の initialValues で key 別に設定済み。

      // データが変更されたときに削除されたドキュメントをリセット
      setDeletedDocuments({});
    }
  }, [data, reset]);

  // タブ切り替え時に初期化によるisDirtyの変化を無視
  // CustomFileUploaderなどが初期化時にsetValueを呼び出すが、
  // 現在の値が初期データと同じであればisDirtyはfalseのまま
  const prevTabRef = React.useRef(currentTab);
  const prevIsDirtyRef = React.useRef(isDirty);

  // タブ切り替え直後かどうかを追跡
  const isTabChangeRecentRef = React.useRef(false);
  const tabChangeTimeRef = React.useRef<number>(0);
  const isResettingRef = React.useRef(false); // リセット中フラグ

  const normalizeValueForComparison = (value: any): any => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "string") {
      return value.trim() === "" ? undefined : value;
    }
    if (value instanceof File) {
      return value.name || "file";
    }
    if (Array.isArray(value)) {
      const normalizedItems = value
        .map((item) => normalizeValueForComparison(item))
        .filter((item) => item !== undefined);
      return normalizedItems.length > 0 ? normalizedItems : undefined;
    }
    if (typeof value === "object") {
      if (value.isUrl && typeof value.data === "string") return value.data;
      if (typeof value.url === "string") return value.url;
      if (typeof value.data === "string") return value.data;

      const normalized: any = {};
      Object.keys(value).forEach((key) => {
        if (key === "id" || key === "isUrl") return;
        normalized[key] = normalizeValueForComparison(value[key]);
      });
      return normalized;
    }
    return value;
  };

  const normalizeWithInitialShape = (value: any, shape: any): any => {
    if (shape === undefined || shape === null) {
      return normalizeValueForComparison(value);
    }
    if (Array.isArray(shape)) {
      if (!Array.isArray(value)) return normalizeValueForComparison(value);
      if (shape.length === 0) return normalizeValueForComparison(value);
      return value.map((item) => normalizeWithInitialShape(item, shape[0]));
    }
    if (typeof shape === "object") {
      const normalized: any = {};
      Object.keys(shape).forEach((key) => {
        normalized[key] = normalizeWithInitialShape(value?.[key], shape[key]);
      });
      return normalized;
    }
    return normalizeValueForComparison(value);
  };

  const getNormalizedSnapshots = () => {
    if (!initialDataRef.current) return null;
    const currentValues = getValues();
    const currentValuesWithLotsAndRoads = {
      ...currentValues,
      lots,
      roads,
    };
    const normalizedCurrent = normalizeWithInitialShape(
      currentValuesWithLotsAndRoads,
      initialDataRef.current,
    );
    const normalizedInitial = normalizeWithInitialShape(
      initialDataRef.current,
      initialDataRef.current,
    );
    return { normalizedCurrent, normalizedInitial };
  };

  // isDirtyの変化を監視してログ出力
  React.useEffect(() => {
    // リセット中はチェックをスキップ
    if (isResettingRef.current) {
      prevIsDirtyRef.current = isDirty;
      return;
    }

    if (prevIsDirtyRef.current !== isDirty) {
      // タブ切り替え直後（3秒以内）にisDirtyがtrueになった場合、初期化による変更の可能性が高い
      if (
        isDirty &&
        isTabChangeRecentRef.current &&
        Date.now() - tabChangeTimeRef.current < 3000
      ) {
        // 初期データと比較
        if (initialDataRef.current) {
          const snapshots = getNormalizedSnapshots();
          if (!snapshots) return;
          const { normalizedCurrent, normalizedInitial } = snapshots;

          const isSameAsInitial =
            JSON.stringify(normalizedCurrent) ===
            JSON.stringify(normalizedInitial);

          if (isSameAsInitial) {
            // リセット中フラグを立てる
            isResettingRef.current = true;
            reset(initialDataRef.current, { keepDirty: false });
            // リセット後はisDirtyがfalseになるはずなので、prevIsDirtyRefをfalseに設定
            prevIsDirtyRef.current = false;
            // リセット完了後にフラグを解除（次のレンダリングサイクルで）
            setTimeout(() => {
              isResettingRef.current = false;
            }, 200);
            return;
          }
        }
      }

      prevIsDirtyRef.current = isDirty;
    }
  }, [isDirty, currentTab, getValues, reset, lots, roads]);

  React.useEffect(() => {
    if (prevTabRef.current !== currentTab) {
      // タブ切り替え時に、初期データと比較して同じであればresetを呼び出してisDirtyをリセット
      // これにより、初期化によるisDirtyの変化を防ぐ
      if (initialDataRef.current) {
        const snapshots = getNormalizedSnapshots();
        if (!snapshots) return;
        const { normalizedCurrent, normalizedInitial } = snapshots;

        const isSameAsInitial =
          JSON.stringify(normalizedCurrent) ===
          JSON.stringify(normalizedInitial);

        if (isSameAsInitial) {
          // 初期データと同じなので、resetを呼び出してisDirtyをfalseにリセット
          reset(initialDataRef.current, { keepDirty: false });
        }
      }

      // タブ切り替え直後であることをマーク
      isTabChangeRecentRef.current = true;
      tabChangeTimeRef.current = Date.now();
      prevTabRef.current = currentTab;

      // 3秒後にタブ切り替え直後フラグを解除
      setTimeout(() => {
        isTabChangeRecentRef.current = false;
      }, 3000);
    }
  }, [currentTab, isDirty, getValues, reset, lots, roads]);

  // 定数の空配列（参照の安定性のため）
  const EMPTY_ARRAY: string[] = React.useMemo(() => [], []);

  // 既存のドキュメントデータをメモ化（無限ループ防止）
  const existingDocumentsMap = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    const documentKeys = [
      "kozu",
      "land_registry",
      "building_registry",
      "survey_map",
      "boundary_confirmation_docs",
      "encroachment_memo_docs",
      "construction_notice",
      "inspection_certificate_docs",
      "completion_drawing",
      "structural_calculation",
      "sales_contract",
      "important_matters",
      "owner_change_notice",
      "appraisal_report",
      "engineering_report",
      "property_management_contract",
      "building_management_contract",
      "electricity_contract",
      "industrial_waste_contract",
      "general_waste_contract",
      "electrical_equipment_contract",
      "fire_prevention_plan",
      "overall_fire_prevention_plan",
      "chief_fire_manager_appointment",
      "fire_manager_appointment",
      "specific_building_change",
      "fire_manager_delegation",
      "insurance_certificate_docs",
      "trust_contract",
      "trust_certificate",
      "trust_transfer_consent",
    ];

    if (data) {
      documentKeys.forEach((key) => {
        const docs = (data as any)?.[key];
        if (docs && Array.isArray(docs) && docs.length > 0) {
          // 削除されたドキュメントを除外（インデックス付き）
          const deletedItems = deletedDocuments[key] || [];
          const filtered = docs.filter((url: string, urlIndex: number) => {
            // このインデックスの書類が削除されているかチェック
            return !deletedItems.some(
              (item: any) => item.url === url && item.index === urlIndex,
            );
          });
          // 空配列の場合は定数を使用（参照の安定性）
          map[key] = filtered.length > 0 ? filtered : EMPTY_ARRAY;
        } else {
          // データがない場合は定数の空配列
          map[key] = EMPTY_ARRAY;
        }
      });

      // その他書類の既存データを追加
      if (data.other_documents && typeof data.other_documents === "object") {
        Object.entries(data.other_documents).forEach(
          ([key, value]: [string, any]) => {
            if (
              value.files &&
              Array.isArray(value.files) &&
              value.files.length > 0
            ) {
              // 削除されたドキュメントを除外（インデックス付き）
              const deletedItems = deletedDocuments[key] || [];
              const filtered = value.files.filter(
                (url: string, urlIndex: number) => {
                  return !deletedItems.some(
                    (item: any) => item.url === url && item.index === urlIndex,
                  );
                },
              );
              map[key] = filtered.length > 0 ? filtered : EMPTY_ARRAY;
            } else {
              map[key] = EMPTY_ARRAY;
            }
          },
        );
      }
    }

    return map;
  }, [data, deletedDocuments, EMPTY_ARRAY]);

  React.useEffect(() => {
    if (chatDocFromState?.fileUrl) {
      const url = chatDocFromState.fileUrl;
      if (chatPreviewOpen) return;
      if (chatFilePickerOpen) return;
      if (lastOpenedChatPreviewUrlRef.current === url) return;
      lastOpenedChatPreviewUrlRef.current = url;
      setChatPreviewFile({
        url,
        name: chatDocFromState.fileName || chatDocFromState.label || "document",
      });
      setChatPreviewOpen(true);
      return;
    }

    if (!docLabel) return;
    if (!data) return;
    if (chatPreviewOpen) return;
    if (chatFilePickerOpen) return;

    const categoryKey = getCategoryKeyByLabel(docLabel);
    const pickUrls = (urls: unknown): string[] => {
      return Array.isArray(urls)
        ? urls.filter((u): u is string => typeof u === "string")
        : [];
    };

    let candidateUrls: string[] = categoryKey
      ? pickUrls(existingDocumentsMap?.[categoryKey])
      : [];

    if (candidateUrls.length === 0) {
      const allUrls = Object.values(existingDocumentsMap || {})
        .flat()
        .filter((u): u is string => typeof u === "string");
      candidateUrls = allUrls.filter((u) =>
        (decodeDocumentNameFromUrl(u) || "").includes(docLabel),
      );
    }

    if (candidateUrls.length === 0) return;

    if (candidateUrls.length === 1) {
      const onlyUrl = candidateUrls[0];
      if (lastOpenedChatPreviewUrlRef.current === onlyUrl) return;
      lastOpenedChatPreviewUrlRef.current = onlyUrl;
      setChatPreviewFile({ url: onlyUrl, name: docLabel });
      setChatPreviewOpen(true);
      return;
    }

    const options = candidateUrls.map((url) => ({
      url,
      name: decodeDocumentNameFromUrl(url) || docLabel,
    }));
    setChatFilePickerOptions(options);
    setChatFilePickerOpen(true);
  }, [
    docLabel,
    data,
    existingDocumentsMap,
    chatPreviewOpen,
    chatFilePickerOpen,
    getCategoryKeyByLabel,
    chatDocFromState,
  ]);

  // タブごとのコンテンツをレンダリング
  const renderTabContent = () => {
    switch (currentTab) {
      case 1: // 詳細情報 (旧「基本情報」+「物件詳細」を統合)
        return (
          <Box>
            {/* 基本情報 (旧「基本情報」サブタブ内容) */}
            <Box mb={3}>
              <CustomSectionTitle title="基本情報" size="small" />
              <Box
                sx={{
                  mt: 1,
                  maxWidth: "100%",
                }}
              >
                <CustomTextInput
                  label="物件名"
                  name="name"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp={"100%"}
                  isRequired
                />
                <CustomSingleSelect
                  label="種別"
                  name="property_category"
                  control={control}
                  errors={errors}
                  options={propertyCategoryOptions}
                  placeholder="選択してください"
                  labelWidth={labelWidth}
                  inputWidth={leftInputContainerWidth}
                  isRequired
                />
                <CustomCategorySelectSingle
                  label="グループ"
                  labelSuffix={
                    <LabelHelpTooltip title="物件をファンドや管理グループでまとめることができ、予実分析にて各グループごとの予実を確認できます。" />
                  }
                  name="group_name"
                  options={groupOptions}
                  selectedValue={groupValue}
                  onValueChange={(val) =>
                    setValue("group_name", val, {
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }
                  onAddNewOption={handleAddNewGroup}
                  labelWidth={labelWidth}
                  inputWidth={leftInputContainerWidth}
                />
                <PostalCodeAutoAddressInput
                  register={register}
                  errors={errors}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  setError={setError}
                  clearErrors={clearErrors}
                  prefectureOptions={prefectureOptions}
                  postalCodeFieldName="postal_code"
                  prefectureFieldName="prefecture"
                  cityFieldName="city"
                  blockNumberFieldName="block_number"
                  postalCodeLabel="郵便番号"
                  prefectureLabel="都道府県"
                  cityLabel="市区町村・番地"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp={"100%"}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  autoSearchEnabled={false}
                  showSearchButton={true}
                  showReverseSearchButton={true}
                  autoReverseSearchEnabled={false}
                  reversePostalCodeLookup={reverseLookupPostalCode}
                />
                <CustomTextInput
                  label="建物名、部屋番号"
                  name="block_number"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp={"100%"}
                />
                <Box sx={{ mt: 2 }}>
                  <CustomFileUploader
                    name="image_urls"
                    register={register as any}
                    setValue={setValue as any}
                    initialImages={(() => {
                      const rawImages = (getValues("image_urls") as any) || [];
                      const filtered = rawImages.filter(Boolean);
                      return filtered;
                    })()}
                    showLabel="true"
                    width="180px"
                    errors={errors}
                    label="物件画像"
                    onImageChange={() => {
                      const currentImages = getValues("image_urls") || [];
                      setValue("image_urls", currentImages, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* 土地情報 (旧「物件詳細」サブタブから統合) */}
            <Box mb={3}>
              <CustomSectionTitle title="土地情報" size="small" />
              <Box
                sx={{
                  mt: 1,
                  maxWidth: "100%",
                }}
              >
                <CustomAutocomplete
                  label="地目"
                  name="land_category"
                  placeholder="入力または選択してください"
                  control={control}
                  options={landCategoryOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="現況"
                  name="current_condition"
                  placeholder="入力または選択してください"
                  control={control}
                  options={currentConditionOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="権利"
                  name="land_rights"
                  placeholder="入力または選択してください"
                  control={control}
                  options={landRightsOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="境界確定"
                  name="boundary_confirmed"
                  placeholder="入力または選択してください"
                  control={control}
                  options={boundaryConfirmedOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="敷地面積(公簿)"
                  name="land_area_registered"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                  inputMode="decimal"
                />
                <CustomTextInput
                  label="敷地面積(実測)"
                  name="land_area_measured"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                  inputMode="decimal"
                />
                {/* 地番と境界情報 */}
                <Box sx={{ mb: 2 }}>
                  <LotBoundaryManager
                    lots={lots}
                    onLotsChange={(newLots) => {
                      setLots(newLots);
                      // フォームにも変更を通知してdirtyにする
                      setValue("lots" as any, newLots, { shouldDirty: true });
                    }}
                  />
                </Box>

                {/* 接道情報: AI 画面コンテキスト用に data-collection="roads" でラップ。
                     CustomThreeColInputGroup は自動付与対象外のため、各行に隠し span で
                     direction / boundary_confirmed / width の data-field を提供する */}
                <Box
                  sx={{ mb: 2, width: { xs: "100%", sm: "600px" } }}
                  data-collection="roads"
                  data-collection-label="接道情報"
                  data-collection-count={roads.length}
                >
                  {roads.map((road, index) => (
                    <div
                      data-collection-item
                      key={`road-${index}`}
                      style={{ display: "contents" }}
                    >
                      <CustomThreeColInputGroup
                        title={index === 0 ? "接道情報" : undefined}
                        firstPlaceholder="例: 南側"
                        secondPlaceholder="選択してください"
                        thirdPlaceholder="例: 10"
                        firstLabel="接道面"
                        secondLabel="境界確定"
                        thirdLabel="道路幅員"
                        secondFieldType="select"
                        secondFieldOptions={[
                          { label: "あり", value: "あり" },
                          { label: "なし", value: "なし" },
                        ]}
                        thirdUnit="m"
                        index={index}
                        onRemove={
                          roads.length > 1 ? () => removeRoad(index) : undefined
                        }
                        values={{
                          first: road.direction,
                          second: road.boundary_confirmed || "",
                          third: road.width,
                        }}
                        onChange={(field, value) => {
                          const newRoads = [...roads];
                          if (field === "first") {
                            newRoads[index].direction = value;
                            setValue(
                              `roads.${index}.direction` as keyof PropertyFormValues,
                              value,
                              {
                                shouldDirty: true,
                              },
                            );
                          } else if (field === "second") {
                            newRoads[index].boundary_confirmed = value;
                            setValue(
                              `roads.${index}.boundary_confirmed` as keyof PropertyFormValues,
                              value,
                              {
                                shouldDirty: true,
                              },
                            );
                          } else if (field === "third") {
                            newRoads[index].width = value;
                            setValue(
                              `roads.${index}.width` as keyof PropertyFormValues,
                              value,
                              {
                                shouldDirty: true,
                              },
                            );
                          }
                          setRoads(newRoads);
                        }}
                        inputWidth="223px"
                        inputModes={{ third: "decimal" }}
                      />
                      {/* AI 用に値を非表示で公開 */}
                      <span
                        style={{ display: "none" }}
                        data-field="direction"
                        data-label="接道面"
                        data-value-raw={road.direction || ""}
                      />
                      <span
                        style={{ display: "none" }}
                        data-field="boundary_confirmed"
                        data-label="境界確定"
                        data-value-raw={road.boundary_confirmed || ""}
                      />
                      <span
                        style={{ display: "none" }}
                        data-field="width"
                        data-label="道路幅員"
                        data-value-raw={road.width || ""}
                      />
                    </div>
                  ))}
                  <Box sx={{ mt: 1 }}>
                    <CustomButton
                      label="＋接道を追加"
                      buttonCategory="clearPrimary"
                      onClick={addRoad}
                      sx={{
                        width: { xs: "100%", sm: "600px" },
                        borderStyle: "dashed",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* 建物情報 */}
            <Box mb={3}>
              <CustomSectionTitle title="建物情報" size="small" />
              <Box
                sx={{
                  mt: 2,
                  maxWidth: "100%",
                }}
              >
                <CustomAutocomplete
                  label="家屋番号"
                  name="house_registration_number"
                  placeholder="入力または選択してください"
                  control={control}
                  options={houseRegistrationNumberOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="建物 種類"
                  name="building_type"
                  placeholder="入力または選択してください"
                  control={control}
                  options={buildingTypeOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="構造"
                  name="structure"
                  placeholder="入力または選択してください"
                  control={control}
                  options={structureOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />

                <CustomTextInput
                  label="地下(階)"
                  name="basement_floors"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                  inputMode="numeric"
                />
                <CustomTextInput
                  label="地上(階)"
                  name="ground_floors"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="建築面積"
                  name="building_area"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="専有面積"
                  name="private_area"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="延床面積(公簿)"
                  name="total_floor_area"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="延床面積(実測)"
                  name="total_floor_area_measured"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="竣工年月日"
                  name="built_date"
                  placeholder="YYYY/MM"
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                  inputMode="numeric"
                />
                <CustomAutocomplete
                  label="検査済証"
                  name="inspection_certificate"
                  placeholder="入力または選択してください"
                  control={control}
                  options={houseRegistrationNumberOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="環境認証"
                  name="environmental_certification"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
              </Box>
            </Box>

            {/* 設備情報 */}
            <Box mb={3}>
              <CustomSectionTitle title="設備情報" size="small" />
              <Box
                sx={{
                  mt: 2,
                  maxWidth: "100%",
                }}
              >
                <CustomTextInput
                  label="設備 電気"
                  name="equipment_electricity"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="設備 上水道"
                  name="equipment_water_supply"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="設備 下水道"
                  name="equipment_sewerage"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomTextInput
                  label="設備 都市ガス"
                  name="equipment_city_gas"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
              </Box>
            </Box>

            {/* 都市計画情報 */}
            <Box mb={3}>
              <CustomSectionTitle title="都市計画情報" size="small" />
              <Box
                sx={{
                  mt: 2,
                  maxWidth: "100%",
                }}
              >
                <CustomAutocomplete
                  label="用途地域"
                  name="usage_zone"
                  placeholder="入力または選択してください"
                  control={control}
                  options={usageZoneOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="防火指定"
                  name="fire_prevention"
                  placeholder="入力または選択してください"
                  control={control}
                  options={firePreventionOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="高度地区"
                  name="height_district"
                  placeholder="入力または選択してください"
                  control={control}
                  options={heightDistrictOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="建蔽率(%)"
                  name="building_coverage_ratio"
                  placeholder="入力または選択してください"
                  control={control}
                  options={buildingCoverageRatioOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
                <CustomAutocomplete
                  label="容積率(%)"
                  name="floor_area_ratio"
                  placeholder="入力または選択してください."
                  control={control}
                  options={floorAreaRatioOptions}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp="100%"
                />
              </Box>
            </Box>

            {/* その他 */}
            <Box mb={8}>
              <CustomSectionTitle title="その他" size="small" />
              <Box
                sx={{
                  mt: 2,
                  maxWidth: "100%",
                }}
              >
                <CustomTextInput
                  label="備考"
                  name="remarks"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp={"100%"}
                  multiline={true}
                  autoResize={true}
                  minRows={3}
                  maxRows={20}
                />
              </Box>
            </Box>
          </Box>
        );
      case 2: // 書類
        return (
          <Box sx={{ pb: 10 }}>
            <CustomSectionTitle title="書類" size="small" />
            <Box sx={{ mt: 1.5 }}>
              <DocumentUploadAccordion
                register={register}
                setValue={setValue}
                errors={errors}
                watch={watch}
                documentGroups={propertyDocumentGroups}
                existingDocuments={existingDocumentsMap}
                otherDocuments={otherDocuments}
                onAddOtherDocument={handleAddOtherDocument}
                focusCategoryLabel={docLabel || undefined}
                onDownload={(doc) => {
                  if (doc.file) {
                    const url = URL.createObjectURL(doc.file);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = doc.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } else if (doc.url) {
                    handleDownloadDocument({
                      url: doc.url,
                      name: doc.name,
                    });
                  }
                }}
                onDocumentDeleted={(
                  key: string,
                  deletedUrl: string,
                  index?: number,
                ) => {
                  // 削除されたドキュメントを追跡（インデックス付き）
                  setDeletedDocuments((prev) => ({
                    ...prev,
                    [key]: [...(prev[key] || []), { url: deletedUrl, index }],
                  }));
                }}
              />
            </Box>
            {chatPreviewFile && (
              <CustomFileModal
                open={chatPreviewOpen}
                onClose={() => {
                  setChatPreviewOpen(false);
                  setChatPreviewFile(null);
                  lastOpenedChatPreviewUrlRef.current = "";
                  navigate(
                    { search: `?${searchParams.toString()}` },
                    {
                      replace: true,
                      state: getCleanedLocationState(),
                    },
                  );
                }}
                imageSrc={chatPreviewFile.url}
                imageName={chatPreviewFile.name}
              />
            )}
            <Dialog
              open={chatFilePickerOpen}
              onClose={() => {
                setChatFilePickerOpen(false);
                setChatFilePickerOptions([]);
                lastOpenedChatPreviewUrlRef.current = "";
                navigate(
                  { search: `?${searchParams.toString()}` },
                  {
                    replace: true,
                    state: getCleanedLocationState(),
                  },
                );
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>{docLabel || "書類を選択"}</DialogTitle>
              <List>
                {chatFilePickerOptions.map((opt) => (
                  <ListItemButton
                    key={opt.url}
                    onClick={() => {
                      setChatFilePickerOpen(false);
                      setChatFilePickerOptions([]);
                      if (lastOpenedChatPreviewUrlRef.current !== opt.url) {
                        lastOpenedChatPreviewUrlRef.current = opt.url;
                      }
                      setChatPreviewFile({ url: opt.url, name: opt.name });
                      setChatPreviewOpen(true);
                    }}
                  >
                    <ListItemText primary={opt.name} />
                  </ListItemButton>
                ))}
              </List>
            </Dialog>
          </Box>
        );
      case 0: // 物件構成 (タスク④/⑤)
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <CustomSectionTitle title="物件構成" />
            </Box>
            <PropertyComposition data={data} />
          </Box>
        );
      default:
        return null;
    }
  };

  // その他書類の枠を「ローカル追加」する。即時APIは走らせず、物件フォームの保存(更新)時に
  // まとめて client 単位へ永続化する (設定ページの「ローカル追加 → 保存」と同じ作法)。
  const handleAddOtherDocument = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const key = `prop-${Date.now()}`;
    setOtherDocuments((prev) => [...prev, { key, label: trimmed }]);
    // フォームを dirty にして「更新」ボタンを有効化 (保存時に upsert)。
    setValue("_otherDocumentsChanged" as any, Date.now(), {
      shouldDirty: true,
    });
  };

  // フォーム送信時にその他書類情報も含める
  const handleFormSubmit = handleSubmit(async (data) => {
    if (!data.property_category?.trim()) {
      setError("property_category", {
        type: "required",
        message: "種別は必須です",
      });
      return;
    }

    const submitData = { ...data };

    // ダミーフィールドを削除（バックエンドに送らない）
    delete (submitData as any)._otherDocumentsChanged;

    // 地番・境界データと接道データを追加
    (submitData as any).lots = lots;
    (submitData as any).roads = roads;

    // ドキュメントキーのリスト（バックエンドAPIの仕様に合わせる）
    const documentKeys = [
      // 登記、測量関連
      "kozu",
      "land_registry",
      "building_registry",
      "survey_map",
      "boundary_confirmation_docs",
      "encroachment_memo_docs",
      // 建築関連
      "construction_notice",
      "inspection_certificate_docs",
      "completion_drawing",
      "structural_calculation",
      // 売買、契約関連
      "sales_contract",
      "important_matters",
      "owner_change_notice",
      "insurance_certificate_docs",
      // デューデリジェンス関連
      "appraisal_report",
      "engineering_report",
      // 管理契約関連
      "property_management_contract",
      "building_management_contract",
      "electricity_contract",
      "industrial_waste_contract",
      "general_waste_contract",
      "electrical_equipment_contract",
      // 消防、防災関連
      "fire_prevention_plan",
      "overall_fire_prevention_plan",
      "chief_fire_manager_appointment",
      "fire_manager_appointment",
      "specific_building_change",
      "fire_manager_delegation",
      // 信託関連
      "trust_contract",
      "trust_certificate",
      "trust_transfer_consent",
    ];

    // 既存ドキュメントから削除されたURLを除外する
    documentKeys.forEach((key) => {
      // 初期データ（既存のURL）を取得
      const initialDocs = (initialDataRef.current as any)?.[key] || [];
      // フォームの値（新規アップロードされたFile）を取得
      const currentValue = submitData[key as keyof typeof submitData];
      // 削除されたアイテムのリスト（インデックス付き）
      const deletedItems = deletedDocuments[key] || [];

      // 新規アップロードされたFileがある場合はそれを保持
      // 既存のURLから削除されたものを除外（インデックスで判定）
      const remainingUrls = Array.isArray(initialDocs)
        ? initialDocs.filter(
            (url: string, urlIndex: number) =>
              !deletedItems.some(
                (item) => item.url === url && item.index === urlIndex,
              ),
          )
        : [];

      // File配列（新規アップロード）がある場合はそのまま
      if (
        currentValue &&
        Array.isArray(currentValue) &&
        currentValue.length > 0
      ) {
        // 新規ファイルがある場合、既存URLと新規Fileを区別するため
        // バックエンドが期待する形式に応じて調整が必要
        // ここでは新規Fileのみを送信（既存URLは別で管理）
        (submitData as any)[key] = currentValue;
      } else {
        // 新規ファイルがない場合は、残っている既存URLを設定
        (submitData as any)[key] =
          remainingUrls.length > 0 ? remainingUrls : [];
      }
    });

    // 初期データにドキュメントがあったが、現在のフォーム値が空の場合、空配列を設定
    if (initialDataRef.current) {
      documentKeys.forEach((key) => {
        const initialDocs = (initialDataRef.current as any)?.[key] || [];
        const currentValue = submitData[key as keyof typeof submitData];
        const deletedItems = deletedDocuments[key] || [];

        // 初期データがあり、すべて削除された場合（インデックスで判定）
        if (
          initialDocs &&
          Array.isArray(initialDocs) &&
          initialDocs.length > 0 &&
          deletedItems.length > 0 &&
          initialDocs.every((url: string, urlIndex: number) =>
            deletedItems.some(
              (item) => item.url === url && item.index === urlIndex,
            ),
          ) &&
          (!currentValue ||
            (Array.isArray(currentValue) && currentValue.length === 0))
        ) {
          (submitData as any)[key] = [];
        }
      });
    }

    // その他書類の情報を追加
    (submitData as any)._otherDocuments = otherDocuments; // メタ情報として追加

    // 「その他」セクションのドキュメントについても削除処理を適用
    otherDocuments.forEach((doc) => {
      const key = doc.key;
      // その他セクションのドキュメントは other_documents オブジェクト内に保存されている
      const initialDocs = (data as any)?.other_documents?.[key]?.files || [];
      const currentValue = submitData[key as keyof typeof submitData];
      const deletedItems = deletedDocuments[key] || [];

      const remainingUrls = Array.isArray(initialDocs)
        ? initialDocs.filter(
            (url: string, urlIndex: number) =>
              !deletedItems.some(
                (item) => item.url === url && item.index === urlIndex,
              ),
          )
        : [];

      if (
        currentValue &&
        Array.isArray(currentValue) &&
        currentValue.length > 0
      ) {
        (submitData as any)[key] = currentValue;
      } else {
        (submitData as any)[key] =
          remainingUrls.length > 0 ? remainingUrls : [];
      }
    });

    // 削除された「その他」セクションのフィールドに空配列を設定
    const initialOtherDocKeys = initialOtherDocumentsRef.current.map(
      (doc) => doc.key,
    );
    const currentOtherDocKeys = otherDocuments.map((doc) => doc.key);
    initialOtherDocKeys.forEach((key) => {
      if (!currentOtherDocKeys.includes(key)) {
        // 削除された「その他」セクションのフィールドに空配列を設定
        (submitData as any)[key] = [];
      }
    });

    await onSubmit(submitData);

    // 親の保存が成功した後で、未登録のグループだけまとめてサーバへ反映する。
    // (handleAddNewGroup はローカル追加のみ)
    const beGroups: string[] = Array.isArray(groupOptionRecord?.groups)
      ? groupOptionRecord.groups
      : [];
    const localGroupValues = groupOptions.map((o) => o.value);
    const hasNewGroup = localGroupValues.some((v) => !beGroups.includes(v));
    if (hasNewGroup) {
      const merged = Array.from(new Set([...beGroups, ...localGroupValues]));
      dispatch(
        upsertGroupOptionManagement({
          existingId: groupOptionRecord?.group_option_management_id,
          groups: merged,
        }),
      ).catch(() => {});
    }

    // その他書類の枠 (client単位) も、ローカル追加した分を保存後にまとめて upsert する。
    // (handleAddOtherDocument はローカル追加のみ。設定ページと同じ「追加 → 保存で確定」)
    const framePayload = otherDocuments.map((d, i) => ({
      document_type: d.key,
      display_name: d.label,
      order: i + 1,
    }));
    const currentDefs = propertyOtherDocDefs ?? [];
    const framesChanged =
      framePayload.length !== currentDefs.length ||
      framePayload.some(
        (f, i) =>
          f.document_type !== currentDefs[i]?.document_type ||
          f.display_name !== currentDefs[i]?.display_name,
      );
    if (framesChanged) {
      dispatch(
        upsertDocumentManagement({
          existingId: documentManagementRecord?.document_management_id,
          documents: documentManagementRecord?.documents ?? {
            individual: [],
            corporate: [],
          },
          property_other_documents: framePayload,
        }) as any,
      ).catch(() => {});
    }
  }, scrollToFirstError);

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isDirty}
      isSubmitting={isSubmitting}
    >
      {() => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Box
            component="form"
            // 防御: タブ内に埋め込まれた子モーダル/ドロワーの <form> submit が
            // React ツリー経由でここまで伝播しても、物件更新を発火させない。
            // 自分自身が submit 元のときだけ処理する。
            onSubmit={(e) => {
              if (e.target !== e.currentTarget) return;
              handleFormSubmit(e);
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <PropertyRegistrationTabs
              currentTab={currentTab}
              tabs={tabs}
              onTabChange={handleTabChange}
              disableConfirm={true}
              hasFixedActionBar={true}
              scrollableContent
            >
              {renderTabContent()}
            </PropertyRegistrationTabs>

            {/* 固定アクションバー（詳細情報・書類タブのみ。物件構成タブは閲覧用なので非表示） */}
            {currentTab > 0 && (
              <FixedActionBar
                submitLabel="更新"
                isLoading={isSubmitting}
                disabled={!isDirty || isSubmitting}
              />
            )}
          </Box>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default PropertyOverview;
